/**
 * ACCOUNT SETTINGS
 * ================
 *
 * Email address and password changes for the signed-in user.
 *
 * Both go through Neon Auth rather than the profiles table: the address here
 * is the sign-in identity, not the display copy carried on the profile row.
 */

import { useState, type FormEvent } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { Check, Eye, EyeOff, Mail, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../src/hooks/useAuth";
import {
  getPasswordStrength,
  isValidEmail,
  MIN_PASSWORD_STRENGTH_SCORE,
} from "../src/lib/validations";

interface PasswordErrors {
  current?: string;
  next?: string;
  confirm?: string;
  general?: string;
}

const PASSWORD_REQUIREMENT_LABELS: Array<{
  key: "length" | "uppercase" | "lowercase" | "number" | "special";
  label: string;
}> = [
  { key: "length", label: "8+ characters" },
  { key: "uppercase", label: "Uppercase" },
  { key: "lowercase", label: "Lowercase" },
  { key: "number", label: "Number" },
  { key: "special", label: "Special character" },
];

export function AccountSettings() {
  const { user, changeEmail, changePassword } = useAuth();

  const currentEmail = user?.email ?? "";
  const emailVerified = !!user?.email_confirmed_at;

  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailNotice, setEmailNotice] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({});
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [visible, setVisible] = useState({ current: false, next: false, confirm: false });

  const strength = getPasswordStrength(passwordForm.next);

  const handleEmailSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setEmailNotice("");

    const nextEmail = newEmail.trim();
    if (!nextEmail) {
      setEmailError("Enter a new email address");
      return;
    }
    if (!isValidEmail(nextEmail)) {
      setEmailError("Enter a valid email address");
      return;
    }
    if (nextEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setEmailError("That is already your email address");
      return;
    }

    setEmailError("");
    setEmailSubmitting(true);
    try {
      const { error, verificationRequired } = await changeEmail(nextEmail);
      if (error) {
        setEmailError(error.message);
        toast.error("Failed to update email: " + error.message);
        return;
      }
      setNewEmail("");
      if (verificationRequired) {
        setEmailNotice(
          `Confirm the change from the link sent to ${currentEmail}. You keep signing in with ${currentEmail} until you do.`,
        );
        toast.success("Confirmation email sent");
      } else {
        setEmailNotice(`Your email address is now ${nextEmail}.`);
        toast.success("Email address updated");
      }
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const errors: PasswordErrors = {};
    if (!passwordForm.current) {
      errors.current = "Enter your current password";
    }
    if (!passwordForm.next) {
      errors.next = "Enter a new password";
    } else if (strength.score < MIN_PASSWORD_STRENGTH_SCORE) {
      errors.next = "Password is too weak. Please meet more requirements.";
    } else if (passwordForm.next === passwordForm.current) {
      errors.next = "New password must be different from the current one";
    }
    if (passwordForm.confirm !== passwordForm.next) {
      errors.confirm = "Passwords do not match";
    }

    setPasswordErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setPasswordSubmitting(true);
    try {
      const { error } = await changePassword(passwordForm.current, passwordForm.next);
      if (error) {
        setPasswordErrors({ general: error.message });
        toast.error("Failed to update password: " + error.message);
        return;
      }
      setPasswordForm({ current: "", next: "", confirm: "" });
      setVisible({ current: false, next: false, confirm: false });
      toast.success("Password updated. Other devices have been signed out.");
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const renderPasswordField = (
    field: "current" | "next" | "confirm",
    label: string,
    autoComplete: string,
  ) => (
    <div className="space-y-2">
      <Label htmlFor={`password-${field}`}>{label}</Label>
      <div className="relative">
        <Input
          id={`password-${field}`}
          type={visible[field] ? "text" : "password"}
          autoComplete={autoComplete}
          value={passwordForm[field]}
          onChange={(e) => setPasswordForm({ ...passwordForm, [field]: e.target.value })}
          className={passwordErrors[field] ? "border-destructive pr-10" : "pr-10"}
          disabled={passwordSubmitting}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
          onClick={() => setVisible({ ...visible, [field]: !visible[field] })}
          aria-label={visible[field] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        >
          {visible[field] ? (
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Eye className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </div>
      {passwordErrors[field] && (
        <p className="text-sm text-destructive">{passwordErrors[field]}</p>
      )}
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email Address
          </CardTitle>
          <CardDescription>The address you sign in with and receive notifications at</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Current email</p>
            <div className="flex items-center gap-2">
              <p className="text-sm">{currentEmail || "Not available"}</p>
              <Badge variant={emailVerified ? "secondary" : "outline"}>
                {emailVerified ? "Verified" : "Unverified"}
              </Badge>
            </div>
          </div>

          <Separator />

          {/* noValidate: the input is type=email for the mobile keyboard, but
              the message below it should be ours, not the browser's bubble. */}
          <form onSubmit={handleEmailSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="new-email">New email address</Label>
              <Input
                id="new-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className={emailError ? "border-destructive" : ""}
                disabled={emailSubmitting}
              />
              {emailError && <p className="text-sm text-destructive">{emailError}</p>}
              {emailVerified && !emailError && (
                <p className="text-sm text-muted-foreground">
                  A confirmation link goes to your current address. The change applies once you
                  follow it.
                </p>
              )}
            </div>

            {emailNotice && (
              <Alert>
                <AlertDescription>{emailNotice}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={emailSubmitting}>
              {emailSubmitting ? "Updating..." : "Update Email"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Password
          </CardTitle>
          <CardDescription>
            Changing your password signs you out everywhere else
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            {renderPasswordField("current", "Current password", "current-password")}
            {renderPasswordField("next", "New password", "new-password")}

            {passwordForm.next && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {PASSWORD_REQUIREMENT_LABELS.map(({ key, label }) => (
                  <div
                    key={key}
                    className={`flex items-center space-x-1 ${
                      strength.requirements[key] ? "text-green-600" : "text-muted-foreground"
                    }`}
                  >
                    {strength.requirements[key] ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <X className="h-3 w-3" />
                    )}
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}

            {renderPasswordField("confirm", "Confirm new password", "new-password")}

            {passwordErrors.general && (
              <Alert variant="destructive">
                <AlertDescription>{passwordErrors.general}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" disabled={passwordSubmitting}>
              {passwordSubmitting ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
